import { api } from '../../../api/client';
import { openReportPreview } from '../../../utils/reportPreview';
import type {
  InvoiceBatchCreated, InvoiceBatchPlan, InvoiceBatchRequest, InvoiceOption,
  InvoiceReviewOutcome, InvoiceReviewSaveRequest, InvoiceValidationRow, RepricedSavedInvoice, SavedInvoice,
  InvoiceCreateResult, InvoiceDraftRequest, InvoiceDraftResponse, InvoiceQueueRow,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Invoicing — API calls.
// Backend: `Api/Controllers/V1/Accounting/InvoicingController.cs`.
// The shared axios client injects the JWT and drives the global loader (§18.6).
// ─────────────────────────────────────────────────────────────────────────────

interface Envelope<T> { success: boolean; data: T }

/** The Invoice Queue (nav `acct-invoice-queue`). */
export async function fetchInvoiceQueue(): Promise<InvoiceQueueRow[]> {
  const res = await api.get<Envelope<InvoiceQueueRow[]>>('/api/v1/invoicing/queue');
  return res.data.data ?? [];
}

/**
 * A new invoice for the selected jobs, priced by the server. Called again after
 * every edit; `silent` keeps the recalculation off the global loader so typing
 * a discount does not flash the page.
 */
export async function draftInvoice(request: InvoiceDraftRequest, silent = false): Promise<InvoiceDraftResponse> {
  const res = await api.post<Envelope<InvoiceDraftResponse>>('/api/v1/invoicing/draft', request,
    silent ? ({ silentLoading: true } as never) : undefined);
  return res.data.data;
}

/** Create the invoice. */
export async function createInvoice(request: InvoiceDraftRequest): Promise<InvoiceCreateResult> {
  const res = await api.post<Envelope<InvoiceCreateResult>>('/api/v1/invoicing', request);
  return res.data.data;
}

/**
 * Invoice Creation Method popup — the ticked jobs grouped into proposed
 * invoices by the server. Called on every option change, off the global loader.
 */
export async function planInvoiceBatch(request: InvoiceBatchRequest): Promise<InvoiceBatchPlan> {
  const res = await api.post<Envelope<InvoiceBatchPlan>>('/api/v1/invoicing/batch/plan', request,
    { silentLoading: true } as never);
  return res.data.data;
}

/** Create the proposed invoices — all of them, or none. */
export async function createInvoiceBatch(request: InvoiceBatchRequest): Promise<InvoiceBatchCreated[]> {
  const res = await api.post<Envelope<InvoiceBatchCreated[]>>('/api/v1/invoicing/batch', request);
  return res.data.data ?? [];
}

/**
 * Preview the UNSAVED invoice: the server renders the draft with the
 * Invoicing_Reports layout and answers with a preview ticket, which opens in a
 * new tab through the shared utils/reportPreview helper.
 */
export async function previewInvoice(request: InvoiceDraftRequest): Promise<boolean> {
  const res = await api.post('/api/v1/invoicing/preview', request);
  return openReportPreview(res.data);
}

/**
 * "Set price" — create the missing default price through the existing Settings ›
 * Test Pricing › Default Pricing endpoint (one source of truth, §19.2).
 * `test` is the TestMethod; Default Pricing keys Method on the same TestMethod.
 */
export async function createDefaultPrice(input: {
  matrix: string; test: string; isGroup: boolean; component: string | null;
  tat: string; priority: string; surchargePrice: number;
}): Promise<void> {
  await api.post('/api/v1/default-pricings', {
    matrix: input.matrix,
    test: input.test,
    isGroup: input.isGroup,
    method: input.isGroup ? null : input.test,
    component: input.isGroup ? null : input.component,
    tat: [input.tat],
    chargeType: '0',                 // Test
    priority: input.priority,
    surcharge: 0,
    surchargePrice: input.surchargePrice,
    invoiceCode: null,
    remark: 'Added from New Invoice',
  });
}

// ── Invoice Validation ────────────────────────────────────────────────────────

const VALIDATION = '/api/v1/invoicing/validation';

/** The Validation list (Pending Review), or its History (reviewed, by Date Invoiced). */
export async function fetchValidation(history = false, dateFilter = '1M'): Promise<InvoiceValidationRow[]> {
  const res = await api.get<Envelope<InvoiceValidationRow[]>>(VALIDATION, { params: { history, dateFilter } });
  return res.data.data ?? [];
}

/** One saved invoice and the Invoiced By choices. */
export async function fetchSavedInvoice(id: string): Promise<{ invoice: SavedInvoice; employees: InvoiceOption[] }> {
  const res = await api.get<Envelope<{ invoice: SavedInvoice; employees: InvoiceOption[] }>>(`${VALIDATION}/${id}`);
  return res.data.data;
}

/** Rows and totals after the reviewer's edits — nothing is stored. */
export async function calculateSavedInvoice(id: string, request: InvoiceReviewSaveRequest): Promise<RepricedSavedInvoice> {
  const res = await api.post<Envelope<RepricedSavedInvoice>>(`${VALIDATION}/${id}/calculate`, request,
    { silentLoading: true } as never);
  return res.data.data;
}

/** Save the Validation detail; answers the invoice as stored. */
export async function saveReviewedInvoice(id: string, request: InvoiceReviewSaveRequest): Promise<SavedInvoice> {
  const res = await api.put<Envelope<SavedInvoice>>(`${VALIDATION}/${id}`, request);
  return res.data.data;
}

/** Review — mailed invoices go to Delivered, the rest to Pending Delivery. */
export async function reviewInvoices(ids: string[]): Promise<{ outcomes: InvoiceReviewOutcome[]; message: string; failed: number }> {
  const res = await api.post<Envelope<{ outcomes: InvoiceReviewOutcome[]; message: string; failed: number }>>(`${VALIDATION}/review`, { ids });
  return res.data.data;
}

/** Delete pending-review invoices; their tests return to the Invoice Queue. */
export async function deleteInvoices(ids: string[]): Promise<void> {
  await api.post(`${VALIDATION}/delete`, { ids });
}

/** Roll Back reviewed invoices to Pending Review. */
export async function rollbackInvoices(ids: string[], reason: string): Promise<string> {
  const res = await api.post<Envelope<{ message: string }>>(`${VALIDATION}/rollback`, { ids, reason });
  return res.data.data.message;
}

/** Render the saved invoice and open it. */
export async function previewSavedInvoice(id: string): Promise<boolean> {
  const res = await api.post(`${VALIDATION}/${id}/preview`);
  return openReportPreview(res.data);
}

/** The server's message from a refused request, when it sent one. */
export function apiErrorMessage(e: unknown): string | null {
  return (e as { response?: { data?: { error?: { message?: string } } } })
    ?.response?.data?.error?.message ?? null;
}
